<script setup lang="ts">
import MidFlowModal from './MidFlowModal.vue';
import BaseButton from './BaseButton.vue';

/**
 * After-hours scheduling explainer modal. Originally inline inside
 * WizardStepDateTime; extracted so RescheduleDialog (PODZ-1698 follow-up)
 * can surface the same "call us to book outside regular hours" hint without
 * duplicating the template.
 *
 * Stays a dumb, presentational component: callers pass `visible` + their
 * tenant phone and listen on `update:visible`. Phone-less tenants render a
 * neutral support fallback, mirroring the wizard's no-availability state.
 */
interface Props {
  visible: boolean;
  tenantPhone: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ 'update:visible': [value: boolean] }>();

function close(): void {
  emit('update:visible', false);
}
</script>

<template>
  <MidFlowModal
    :visible="props.visible"
    title="After-hours scheduling"
    data-testid="after-hours-modal"
    @update:visible="(v) => emit('update:visible', v)"
  >
    <p class="text-base text-foreground mb-2">
      Please call us to book outside of regular hours:
    </p>
    <p
      v-if="props.tenantPhone"
      class="text-lg font-semibold"
      data-testid="after-hours-phone"
    >
      <a
        :href="`tel:${props.tenantPhone}`"
        class="text-primary hover:underline"
        >{{ props.tenantPhone }}</a
      >
    </p>
    <p
      v-else
      class="text-base text-muted"
      data-testid="after-hours-phone-fallback"
    >
      Please contact support to schedule an after-hours appointment.
    </p>
    <template #footer>
      <BaseButton variant="secondary" label="Close" @click="close" />
    </template>
  </MidFlowModal>
</template>
